const Stat = ({ label, value, testid }) => (
  <div className="rounded-md border border-border bg-alabaster p-3" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="mt-1 text-lg font-medium text-charcoal">{value}</p>
  </div>
);

const palletsOf = (l) => [...new Set(l.source_rows.map((s) => s.pallet).filter((p) => p != null))];

const Line = ({ c, l }) => {
  const resolved = l.source_rows.filter((s) => s.resolved_from);
  const pallets = palletsOf(l);
  return (
    <li data-testid={`import-v51-line-${c.container}-${l.sku}`}>
      <span className="font-mono">{l.sku}</span> · {l.variant_label}{l.supplier ? ` · ${l.supplier.name} (${l.supplier.code})` : l.supplier_raw ? ` · supplier "${l.supplier_raw}" (unmatched)` : ""} · <strong>{l.boxes}</strong> boxes
      {pallets.length > 0 && <span className="ml-1 text-warmgrey">· pallet {pallets.join(", ")}</span>}
      {l.source_rows.length > 1 && <span className="ml-1 text-warmgrey">(aggregated from rows {l.source_rows.map((s) => s.row).join(", ")})</span>}
      {resolved.length > 0 && <span className="ml-1 badge-status badge-available" data-testid={`import-v51-resolved-${l.sku}`}>Matched · row {resolved.map((s) => s.row).join(", ")}</span>}
      {!l.us_color_name && !/^(DAM|FIN)\d|^Mosaic /.test(`${l.sku} ${l.variant_label || ""}`) && <span className="ml-1 badge-status badge-neutral">U.S. name pending</span>}
    </li>
  );
};

const Container = ({ c }) => (
  <div className="rounded-md border border-border p-4" data-testid={`import-v51-container-${c.container}`}>
    <div className="flex flex-wrap items-center gap-3">
      <span className="font-mono text-sm text-charcoal">Container {c.container}</span>
      <span className={`badge-status ${c.state === "NEW" ? "badge-available" : "badge-neutral"}`}>{c.state === "NEW" ? "New" : `Existing · ${c.existing_status}`}</span>
      {c.locked && <span className="badge-status badge-sold" data-testid={`import-v51-locked-${c.container}`}>Locked — {c.lock_reason}</span>}
      <span className="text-xs text-warmgrey">Shipping # {c.shipping_number || "—"} · ETA warehouse {c.eta_warehouse || "—"} · {c.lines.length} lines · {c.total_boxes} boxes · {c.total_sqft.toLocaleString()} sq ft</span>
    </div>
    {c.changes.length > 0 && (
      <p className="mt-2 text-xs text-terracotta" data-testid={`import-v51-changes-${c.container}`}>
        Changes: {c.changes.map((ch) => `${ch.sku} ${ch.from ?? 0} → ${ch.to}`).join(", ")}
      </p>
    )}
    <ul className="mt-2 grid grid-cols-1 gap-1 text-xs text-charcoal md:grid-cols-2">
      {c.lines.map((l) => <Line key={`${l.sku}-${l.supplier_id || l.supplier_raw || ""}`} c={c} l={l} />)}
    </ul>
  </div>
);

const Skipped = ({ rows }) => (
  <div data-testid="import-v51-excluded-list">
    <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Not imported ({rows.length})</p>
    <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-charcoal">
      {rows.map((e) => (
        <li key={e.row} data-testid={`import-v51-excluded-row-${e.row}`}>
          Row {e.row} · <span className="font-mono">{e.sku || "—"}</span>{e.description ? ` · ${e.description}` : ""}{e.boxes != null ? ` · ${e.boxes} boxes` : ""} · {e.reason}
        </li>
      ))}
    </ul>
  </div>
);

// ID-077: everything here is the server-computed payload Confirm Import will write (accepted + matched rows)
export const FinalImportSummary = ({ preview, final }) => (
  <>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-6" data-testid="import-v51-final">
      <Stat label="Source rows" value={preview.source_rows} testid="import-v51-source-rows" />
      <Stat label="Rows to import" value={final.accepted_rows} testid="import-v51-accepted" />
      <Stat label="Not imported" value={final.skipped.length} testid="import-v51-excluded" />
      <Stat label="Containers" value={final.containers.filter((c) => c.lines.length).length} testid="import-v51-containers" />
      <Stat label="Boxes to import" value={final.total_boxes} testid="import-v51-boxes" />
      <Stat label="Sq ft to import" value={final.total_sqft.toLocaleString()} testid="import-v51-sqft" />
    </div>
    <p className="text-xs text-warmgrey" data-testid="import-v51-reconcile">
      {final.source_boxes} boxes on the list = {final.total_boxes} to import + {final.excluded_boxes} excluded + {final.unresolved_boxes} unresolved
      {final.resolved_boxes > 0 ? ` · includes ${final.resolved_boxes} matched boxes from ${final.resolved_rows} rows` : ""}
    </p>
    {final.missing_sqft_skus.length > 0 && (
      <p className="text-xs text-terracotta" data-testid="import-v51-missing-sqft">No sq ft per box in the catalog for {final.missing_sqft_skus.join(", ")} — counted as 0 sq ft</p>
    )}
    <div className="space-y-3">{final.containers.map((c) => <Container key={c.container} c={c} />)}</div>
    {final.skipped.length > 0 && <Skipped rows={final.skipped} />}
  </>
);
